/**
 * Sizing/positioning for the "open in new window" popouts (bug report, feature request).
 *
 * The app shell pins html/body/#root to 100vh with overflow hidden (App.css), so a popout
 * asking for more height than the screen can give ends up clipped — the user cannot reach
 * the Submit button at the bottom. Clamp the request to the space the screen actually has
 * and center it there; the popout pages scroll internally for anything still taller.
 */

export interface PopoutGeometry {
  width: number;
  height: number;
  left: number;
  top: number;
}

/** Subset of window.screen we depend on (availLeft/availTop are non-standard but widely present). */
export interface ScreenMetrics {
  availWidth: number;
  availHeight: number;
  availLeft?: number;
  availTop?: number;
}

const MIN_DIMENSION = 360;

export function computePopoutGeometry(
  desiredWidth: number,
  desiredHeight: number,
  screen: ScreenMetrics
): PopoutGeometry {
  const width = Math.max(MIN_DIMENSION, Math.min(desiredWidth, screen.availWidth));
  const height = Math.max(MIN_DIMENSION, Math.min(desiredHeight, screen.availHeight));
  const baseLeft = screen.availLeft ?? 0;
  const baseTop = screen.availTop ?? 0;

  return {
    width,
    height,
    left: Math.round(baseLeft + Math.max(0, (screen.availWidth - width) / 2)),
    top: Math.round(baseTop + Math.max(0, (screen.availHeight - height) / 2)),
  };
}

export function buildPopoutFeatures(geometry: PopoutGeometry): string {
  return [
    'popup=yes',
    `width=${geometry.width}`,
    `height=${geometry.height}`,
    `left=${geometry.left}`,
    `top=${geometry.top}`,
    'menubar=no',
    'toolbar=no',
    'location=no',
    'status=no',
    'scrollbars=yes',
    'resizable=yes',
  ].join(',');
}

/** Open `url` as a right-sized popout window and focus it. */
export function openPopoutWindow(
  url: string,
  name: string,
  desiredWidth = 720,
  desiredHeight = 900
): Window | null {
  const geometry = computePopoutGeometry(desiredWidth, desiredHeight, window.screen);
  const popout = window.open(url, name, buildPopoutFeatures(geometry));
  popout?.focus();
  return popout;
}
